import { HttpStatusCode, isAxiosError } from "axios";

import { NOTIFICATIONS_ENDPOINT, NOTIFICATION_FILTER_PARAMS, NOTIFICATION_PAGE_SIZE } from "../constants";
import { notificationSchema, notificationStatusSchema, notificationsResponseSchema } from "../schemas";
import type {
  GetNotificationsResponse,
  MatchingUnreadCountParams,
  NotificationData,
  NotificationFilterOptionsParams,
  NotificationFilters,
} from "../types";
import { flattenNotificationPages, toLocalDayWindow } from "../utils";

import { URL_PARAMS } from "@/constants/urlParams";
import { instance } from "@/lib/axios/instance";

export function toApiFilterParams(params: NotificationFilterOptionsParams) {
  const { from, toExclusive } = toLocalDayWindow(params);
  return {
    [NOTIFICATION_FILTER_PARAMS.STATUS]: params.status,
    [URL_PARAMS.SEARCH]: params.search,
    [URL_PARAMS.DATE_FROM]: from?.toISOString(),
    [URL_PARAMS.DATE_TO]: toExclusive?.toISOString(),
  };
}

export function toNotificationApiParams(filters: NotificationFilters) {
  return {
    ...toApiFilterParams(filters),
    [NOTIFICATION_FILTER_PARAMS.SEVERITY]: filters.severities,
    [NOTIFICATION_FILTER_PARAMS.ACTOR_ID]: filters.actorIds,
    [NOTIFICATION_FILTER_PARAMS.CLIENT_ID]: filters.clientIds,
  };
}

export async function fetchNotifications(
  page: number,
  filters: NotificationFilters,
): Promise<GetNotificationsResponse> {
  const { data } = await instance.get<unknown>(NOTIFICATIONS_ENDPOINT, {
    params: {
      [URL_PARAMS.PAGE]: page,
      [URL_PARAMS.PAGE_SIZE]: NOTIFICATION_PAGE_SIZE,
      ...toNotificationApiParams(filters),
    },
  });
  return notificationsResponseSchema.parse(data);
}

async function fetchNotificationCount(params: Record<string, unknown>): Promise<number> {
  const { data } = await instance.get<unknown>(NOTIFICATIONS_ENDPOINT, {
    params: { [URL_PARAMS.PAGE]: 1, [URL_PARAMS.PAGE_SIZE]: 1, ...params },
  });
  return notificationsResponseSchema.parse(data).totalRecords;
}

export function fetchUnreadNotificationCount(): Promise<number> {
  return fetchNotificationCount({ [NOTIFICATION_FILTER_PARAMS.STATUS]: notificationStatusSchema.enum.unread });
}

export function fetchMatchingUnreadNotificationCount({ filter, createdAtOrBefore }: MatchingUnreadCountParams) {
  return fetchNotificationCount({
    ...toNotificationApiParams({ ...filter, status: notificationStatusSchema.enum.unread }),
    [NOTIFICATION_FILTER_PARAMS.CREATED_AT_OR_BEFORE]: createdAtOrBefore,
  });
}

export async function fetchNotification(notificationId: NotificationData["id"]): Promise<NotificationData | null> {
  try {
    const { data } = await instance.get<unknown>(`${NOTIFICATIONS_ENDPOINT}/${notificationId}`);
    return notificationSchema.parse(data);
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) return null;
    throw error;
  }
}

export function getNextNotificationsPageParam(
  lastPage: GetNotificationsResponse,
  allPages: GetNotificationsResponse[],
) {
  return flattenNotificationPages(allPages).length < lastPage.totalRecords
    ? allPages.length + 1
    : undefined;
}
